import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Empty, Modal, Input, Spin, Table, Button, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Text } from 'atoms';
import ClientContractCardV2 from './ClientContractCardV2';
import {
	searchPendingApprovals,
	approveClientContract,
	rejectClientContract,
} from 'store/approvalSliceV2';

const PageContainer = styled(Box)`
	max-width: 1600px;
	margin: 0 auto;
	padding: 24px;
`;

const HeaderContainer = styled(Box)`
	margin-bottom: 24px;
`;

const PageTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #333;
	display: block;
	margin-bottom: 4px;
`;

const PageSubtitle = styled(Text)`
	font-size: 14px;
	color: #707070;
	display: block;
`;

const TabsContainer = styled(Box)`
	margin-top: 24px;
`;

const StyledTabs = styled(Tabs)`
	.ant-tabs-tab {
		font-size: 16px;
		font-weight: 500;
		padding: 12px 24px;
	}

	.ant-tabs-tab-active {
		.ant-tabs-tab-btn {
			color: #1890ff;
			font-weight: 600;
		}
	}

	.ant-tabs-ink-bar {
		background: #1890ff;
	}
`;

const ContentContainer = styled(Box)`
	margin-top: 24px;
`;

const EmptyStateContainer = styled(Box)`
	padding: 60px 20px;
	text-align: center;
`;

const LoadingContainer = styled(Box)`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 60px 20px;
`;

const StyledTable = styled(Table)`
	.ant-table-thead > tr > th {
		background-color: #18405d;
		color: #fff;
		font-weight: 600;
	}

	.ant-table-tbody > tr:nth-child(even) {
		background-color: #f5f5f5;
	}

	.ant-table-tbody > tr:hover {
		background-color: #e6f7ff;
	}
`;

const ApproveModalContent = styled(Box)`
	text-align: center;
`;

const ModalTitle = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #000;
	display: block;
	margin-bottom: 16px;
`;

const ModalMessage = styled(Text)`
	font-size: 14px;
	color: #666;
	display: block;
	margin-bottom: 24px;
	line-height: 1.5;
`;

const ModalButtonGroup = styled(Box)`
	display: flex;
	justify-content: center;
	gap: 12px;
	margin-top: 24px;
`;

const CancelButton = styled(Button)`
	border-color: #1890ff;
	color: #1890ff;
	background: #fff;
	min-width: 100px;

	&:hover {
		border-color: #40a9ff;
		color: #40a9ff;
		background: #fff;
	}
`;

const ApproveButtonStyled = styled(Button)`
	background: #1890ff;
	border-color: #1890ff;
	color: #fff;
	min-width: 100px;

	&:hover {
		background: #40a9ff;
		border-color: #40a9ff;
	}
`;

const { TextArea } = Input;

export default function ClientContractApprovalPageV2() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('pending');
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [selectedApproval, setSelectedApproval] = useState(null);
	const [comments, setComments] = useState('');
	const [rejectionReason, setRejectionReason] = useState('');

	const { approvals, loading, actionLoading } = useSelector((state) => state.approvalStateV2);

	const fetchApprovals = useCallback(
		(status) => {
			dispatch(
				searchPendingApprovals({
					filter_by: {
						approval_type: ['client_contract', 'client_contract_edit'],
						status: status === 'pending' ? 'pending' : status,
					},
					pagination: { page: 1, per_page: 20 },
				})
			);
		},
		[dispatch]
	);

	useEffect(() => {
		fetchApprovals(activeTab);
	}, [activeTab, fetchApprovals]);

	const handleTabChange = (key) => {
		setActiveTab(key);
	};

	const handleView = (entityId, approvalId) => {
		// Navigate to approval details page with entity_id (contract ID) in URL
		// Pass approval_id in state for approval context
		if (approvalId && entityId) {
			navigate(`/approvals/client-contract/${entityId}`, {
				state: { fromApproval: true, approvalId, entityId },
			});
		} else if (entityId) {
			// Fallback to contract route if no approval ID
			navigate(`/client-contracts/${entityId}`, {
				state: { fromApproval: true, approvalId },
			});
		}
	};

	const handleApproveClick = (approval) => {
		setSelectedApproval(approval);
		setComments('');
		setApproveModalVisible(true);
	};

	const handleRejectClick = (approval) => {
		setSelectedApproval(approval);
		setRejectionReason('');
		setRejectModalVisible(true);
	};

	const handleApproveConfirm = async () => {
		if (!selectedApproval) return;

		if (!comments.trim()) {
			message.error('Comments are required for approval');
			return;
		}

		// Use approval_id from the approval object
		const approvalId = selectedApproval.id;
		const result = await dispatch(
			approveClientContract({
				approval_id: approvalId,
				comments,
			})
		);
		if (result.success) {
			setApproveModalVisible(false);
			setSelectedApproval(null);
			setComments('');
			// Refresh the list
			fetchApprovals(activeTab);
		} else {
			// Error is already shown by the thunk, but we can keep modal open for user to retry
			// The modal will stay open so user can see the error and try again
		}
	};

	const handleRejectConfirm = async () => {
		if (!selectedApproval || !rejectionReason.trim()) {
			return;
		}

		// Use approval_id from the approval object
		const approvalId = selectedApproval.id;
		const result = await dispatch(
			rejectClientContract({
				approval_id: approvalId,
				rejection_reason: rejectionReason,
			})
		);
		if (result.success) {
			setRejectModalVisible(false);
			setSelectedApproval(null);
			setRejectionReason('');
			// Refresh the list
			fetchApprovals(activeTab);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		return moment(dateString).format('DD-MM-YYYY');
	};

	const formatTime = (dateString) => {
		if (!dateString) return '-';
		return moment(dateString).format('HH:mm');
	};

	const formatTempControlled = (value) => {
		if (
			value === true ||
			value === 'true' ||
			value === 'Yes' ||
			value === 'yes' ||
			value === 1
		) {
			return 'Yes';
		}
		if (
			value === false ||
			value === 'false' ||
			value === 'No' ||
			value === 'no' ||
			value === 0
		) {
			return 'No';
		}
		return value || '-';
	};

	/**
	 * Extract temperature_controlled value from approval data
	 * Handles false values correctly (doesn't convert to null)
	 */
	const getTemperatureControlled = (approval) => {
		if (approval.entity_data?.temperature_controlled !== undefined) {
			return approval.entity_data.temperature_controlled;
		}
		if (approval.entity_summary?.temperature_controlled !== undefined) {
			return approval.entity_summary.temperature_controlled;
		}
		return null;
	};

	const requestTypeColumn = {
		title: 'Request Type',
		dataIndex: 'approval_type',
		key: 'approval_type',
		width: 140,
		render: (type) =>
			type === 'client_contract_edit' ? (
				<Text style={{ color: '#faad14', fontWeight: 500 }}>Edit Request</Text>
			) : (
				<Text>New Contract</Text>
			),
	};

	const getTableColumns = (tabKey) => {
		const baseColumns = [
			requestTypeColumn,
			{
				title: 'Client Name',
				dataIndex: 'client_name',
				key: 'client_name',
				width: 180,
			},
			{
				title: 'Project Name',
				dataIndex: 'project_name',
				key: 'project_name',
				width: 180,
			},
			{
				title: 'Billing Type',
				dataIndex: 'billing_type',
				key: 'billing_type',
				width: 150,
			},
			{
				title: 'Contract Date',
				key: 'contract_date',
				width: 150,
				render: (_, record) => formatDate(record.contract_date),
			},
			{
				title: 'Expiry Date',
				key: 'expiry_date',
				width: 150,
				render: (_, record) => formatDate(record.expiry_date),
			},
			{
				title: 'Temp Controlled',
				key: 'temp_controlled',
				width: 150,
				render: (_, record) => formatTempControlled(record.temp_controlled),
			},
			{
				title: 'Created by',
				dataIndex: 'created_by',
				key: 'created_by',
				width: 150,
			},
		];

		// Add approval columns for approved/rejected tabs
		if (tabKey === 'approved') {
			baseColumns.push(
				{
					title: 'Approved at',
					key: 'approved_at',
					width: 180,
					render: (_, record) =>
						!record.approved_at ? (
							'-'
						) : (
							<>
								<div>{formatDate(record.approved_at)}</div>
								<div style={{ fontSize: '12px', color: '#666' }}>
									{formatTime(record.approved_at)}
								</div>
							</>
						),
				},
				{
					title: 'Approved by',
					dataIndex: 'approved_by',
					key: 'approved_by',
					width: 150,
				}
			);
		} else if (tabKey === 'rejected') {
			baseColumns.push(
				{
					title: 'Rejected at',
					key: 'rejected_at',
					width: 180,
					render: (_, record) =>
						!record.rejected_at && !record.approved_at ? (
							'-'
						) : (
							<>
								<div>{formatDate(record.rejected_at || record.approved_at)}</div>
								<div style={{ fontSize: '12px', color: '#666' }}>
									{formatTime(record.rejected_at || record.approved_at)}
								</div>
							</>
						),
				},
				{
					title: 'Rejected by',
					dataIndex: 'rejected_by',
					key: 'rejected_by',
					width: 150,
					render: (_, record) => record.rejected_by || '-',
				}
			);
		} else {
			// Add view action column only for pending tab
			baseColumns.push({
				title: 'Action',
				key: 'action',
				width: 100,
				fixed: 'right',
				render: (_, record) => (
					<Button
						type="text"
						icon={<EyeOutlined />}
						onClick={() =>
							handleView(record.contract_id || record.id, record.approval_id)
						}
						title="View Details"
					/>
				),
			});
		}

		return baseColumns;
	};

	const mapApprovalToTableData = (approval) => ({
		key: approval.id,
		id: approval.entity_id || approval.id,
		contract_id: approval.entity_id || approval.id,
		approval_id: approval.id, // Store approval_id for navigation
		approval_type: approval.approval_type,
		payload: approval.payload,
		client_name:
			approval.entity_summary?.client_name || approval.entity_data?.client_name || '-',
		project_name:
			approval.entity_summary?.project_name || approval.entity_data?.project_name || '-',
		billing_type:
			approval.entity_summary?.billing_type ||
			approval.entity_data?.billing_type ||
			approval.entity_data?.contract_type ||
			'-',
		contract_date:
			approval.entity_summary?.contract_date || approval.entity_data?.contract_date || null,
		expiry_date:
			approval.entity_summary?.expiry_date || approval.entity_data?.expiry_date || null,
		temp_controlled: getTemperatureControlled(approval),
		created_by: approval.requested_by_name || '-',
		approved_at: approval.approved_at || approval.updated_at || null,
		approved_by: approval.approver_name || '-',
		rejected_at: approval.rejected_at || approval.updated_at || null,
		rejected_by: approval.rejected_by_name || '-',
	});

	const renderTableContent = (tabKey) => {
		const tableData = approvals.map(mapApprovalToTableData);
		const columns = getTableColumns(tabKey);

		return (
			<ContentContainer>
				<StyledTable
					columns={columns}
					dataSource={tableData}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) => `Total ${total} items`,
						position: ['bottomRight'],
					}}
					scroll={{ x: 'max-content' }}
					locale={{
						emptyText: (
							<Empty
								description={
									<Text fontSize="14px" color="#999">
										No {tabKey} client contracts found
									</Text>
								}
							/>
						),
					}}
				/>
			</ContentContainer>
		);
	};

	const renderContent = (tabKey) => {
		if (loading) {
			return (
				<LoadingContainer>
					<Spin size="large" tip="Loading approvals..." />
				</LoadingContainer>
			);
		}

		if (!approvals || approvals.length === 0) {
			return (
				<EmptyStateContainer>
					<Empty
						description={
							<Text fontSize="14px" color="#999">
								No {tabKey} client contracts found
							</Text>
						}
					/>
				</EmptyStateContainer>
			);
		}

		// Show table for approved/rejected tabs, cards for pending
		if (tabKey === 'approved' || tabKey === 'rejected') {
			return renderTableContent(tabKey);
		}

		// Show cards for pending tab
		return (
			<ContentContainer>
				{approvals.map((approval) => {
					// Map approval data to contract format
					const contract = {
						id: approval.entity_id || approval.id,
						client_name:
							approval.entity_summary?.client_name ||
							approval.entity_data?.client_name ||
							'-',
						project_name:
							approval.entity_summary?.project_name ||
							approval.entity_data?.project_name ||
							'-',
						billing_type:
							approval.entity_summary.billing_type ||
							approval.entity_data?.billing_type ||
							approval.entity_data?.contract_type ||
							'-',
						contract_date:
							approval.entity_summary.contract_date ||
							approval.entity_data?.contract_date ||
							null,
						expiry_date:
							approval.entity_summary.expiry_date ||
							approval.entity_data?.expiry_date ||
							null,
						temp_controlled: getTemperatureControlled(approval),
						created_by: approval.requested_by_name || '-',
					};

					return (
						<ClientContractCardV2
							key={approval.id}
							contract={contract}
							onView={() =>
								handleView(approval.entity_id || approval.id, approval.id)
							}
							onApprove={() => handleApproveClick(approval)}
							onReject={() => handleRejectClick(approval)}
							canDecide={approval.can_decide !== false}
						/>
					);
				})}
			</ContentContainer>
		);
	};

	const tabItems = [
		{
			key: 'pending',
			label: 'Pending',
			children: renderContent('pending'),
		},
		{
			key: 'approved',
			label: 'Approved',
			children: renderContent('approved'),
		},
		{
			key: 'rejected',
			label: 'Rejected',
			children: renderContent('rejected'),
		},
	];

	return (
		<PageContainer>
			<HeaderContainer>
				<PageTitle>Client Contract request approvals</PageTitle>
				<PageSubtitle>Approve client contract requests</PageSubtitle>
			</HeaderContainer>

			<TabsContainer>
				<StyledTabs
					activeKey={activeTab}
					onChange={handleTabChange}
					items={tabItems}
					type="line"
				/>
			</TabsContainer>

			{/* Approve Modal */}
			<Modal
				title={null}
				open={approveModalVisible}
				onCancel={() => {
					setApproveModalVisible(false);
					setSelectedApproval(null);
					setComments('');
				}}
				footer={null}
				centered
				width={400}
			>
				<ApproveModalContent>
					<ModalTitle>Are you sure?</ModalTitle>
					<ModalMessage>
						This contract will be approved and you won&apos;t be able to revert this
						change.
					</ModalMessage>
					<Box mb={3}>
						<Text
							fontSize="14px"
							color="#333"
							mb={1}
							display="block"
							style={{ textAlign: 'left' }}
						>
							Comments <Text color="#ff4d4f">*</Text>
						</Text>
						<TextArea
							rows={4}
							value={comments}
							onChange={(e) => setComments(e.target.value)}
							placeholder="Enter any comments for this approval..."
							maxLength={1000}
							showCount
							status={!comments.trim() && comments !== '' ? 'error' : ''}
						/>
						{!comments.trim() && comments !== '' && (
							<Text fontSize="12px" color="#ff4d4f" mt={1} display="block">
								Comments are required
							</Text>
						)}
					</Box>
					<ModalButtonGroup>
						<CancelButton
							onClick={() => {
								setApproveModalVisible(false);
								setSelectedApproval(null);
								setComments('');
							}}
							disabled={actionLoading}
						>
							Cancel
						</CancelButton>
						<ApproveButtonStyled
							type="primary"
							onClick={handleApproveConfirm}
							loading={actionLoading}
							disabled={actionLoading || !comments.trim()}
						>
							Approve
						</ApproveButtonStyled>
					</ModalButtonGroup>
				</ApproveModalContent>
			</Modal>

			{/* Reject Modal */}
			<Modal
				title="Reject Client Contract"
				open={rejectModalVisible}
				onOk={handleRejectConfirm}
				onCancel={() => {
					setRejectModalVisible(false);
					setSelectedApproval(null);
					setRejectionReason('');
				}}
				okText="Reject"
				cancelText="Cancel"
				confirmLoading={actionLoading}
				okButtonProps={{
					disabled: actionLoading || !rejectionReason.trim(),
					danger: true,
				}}
			>
				<Box mb={3}>
					<Text fontSize="14px" color="#666" mb={2} display="block">
						Are you sure you want to reject this client contract?
					</Text>
					{selectedApproval && (
						<Box mt={2} p={2} background="#f5f5f5" borderRadius="4px">
							<Text fontSize="12px" color="#666" display="block">
								Contract ID: {selectedApproval.entity_id || selectedApproval.id}
							</Text>
							<Text fontSize="12px" color="#666" display="block">
								Requested by: {selectedApproval.requested_by_name || '-'}
							</Text>
						</Box>
					)}
				</Box>
				<Box>
					<Text fontSize="14px" color="#333" mb={1} display="block">
						Rejection Reason <Text color="#ff4d4f">*</Text>
					</Text>
					<TextArea
						rows={4}
						value={rejectionReason}
						onChange={(e) => setRejectionReason(e.target.value)}
						placeholder="Enter reason for rejection..."
						maxLength={1000}
						showCount
						status={!rejectionReason.trim() && rejectionReason !== '' ? 'error' : ''}
					/>
					{!rejectionReason.trim() && rejectionReason !== '' && (
						<Text fontSize="12px" color="#ff4d4f" mt={1} display="block">
							Rejection reason is required
						</Text>
					)}
				</Box>
			</Modal>
		</PageContainer>
	);
}
